/**
 * Service d'authentification pour la plateforme Anamnesis
 * Implémente toutes les API d'authentification JWT du backend
 */

import type { ApiError, ApiResponse } from '@/types/api';
import type {
    AdminResetPasswordRequest,
    AuthError,
    ChangePasswordRequest,
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    RegisterResponse,
    User
} from '@/types/auth';
import { apiClient } from './api';

/**
 * Classe de service d'authentification
 */
class AuthService {
  
  /**
   * Connexion utilisateur
   * POST /auth/login
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: ApiResponse<LoginResponse> = await apiClient.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'login');
    }
  }

  /**
   * Inscription utilisateur
   * POST /auth/register
   */
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response: ApiResponse<RegisterResponse> = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'register');
    }
  }

  /**
   * Récupération du profil utilisateur actuel
   * GET /auth/me
   */
  async getCurrentUser(token: string): Promise<User> {
    try {
      const response: ApiResponse<User> = await apiClient.get('/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'fetchUser');
    }
  }

  /**
   * Changement de mot de passe
   * POST /auth/change-password
   */
  async changePassword(passwords: ChangePasswordRequest, token: string): Promise<{ message: string }> {
    try {
      const response: ApiResponse<{ message: string }> = await apiClient.post('/auth/change-password', passwords, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'changePassword');
    }
  }

  /**
   * Réinitialisation de mot de passe par admin
   * POST /auth/admin/reset-password
   */
  async adminResetPassword(data: AdminResetPasswordRequest, token: string): Promise<{ message: string }> {
    try {
      const response: ApiResponse<{ message: string }> = await apiClient.post('/auth/admin/reset-password', data, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'adminReset');
    }
  }

  /**
   * Validation d'email avec token
   * POST /auth/validate-email
   */
  async validateEmail(token: string): Promise<{ message: string }> {
    try {
      const response: ApiResponse<{ message: string }> = await apiClient.post('/auth/validate-email', { token });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'validateEmail');
    }
  }

  /**
   * Demande de réinitialisation de mot de passe par email
   * POST /auth/password-reset-request
   * Note: Endpoint disponible mais non fonctionnel selon la doc
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response: ApiResponse<{ message: string }> = await apiClient.post('/auth/password-reset-request', { email });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'passwordResetRequest');
    }
  }

  /**
   * Confirmation de réinitialisation de mot de passe
   * POST /auth/password-reset-confirm
   * Note: Endpoint disponible mais non fonctionnel selon la doc
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response: ApiResponse<{ message: string }> = await apiClient.post('/auth/password-reset-confirm', {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error as ApiError, 'passwordResetConfirm');
    }
  }

  /**
   * Gestion centralisée des erreurs d'authentification
   */
  private handleAuthError(apiError: ApiError, context: string): AuthError {
    let code: string;
    let message: string;

    // Mapping des erreurs selon le contexte et le status
    switch (apiError.status) {
      case 400: {
        // Erreurs de validation côté serveur (souvent pour l'inscription)
        const errorMsg = apiError.message.toLowerCase();
        
        if (errorMsg.includes('email already registered') || errorMsg.includes('already exists')) {
          code = 'EMAIL_ALREADY_EXISTS';
          message = 'Un compte existe déjà avec cet email';
        } else if (errorMsg.includes('email not verified')) {
          code = 'EMAIL_NOT_VERIFIED';
          message = 'Votre email n\'a pas été validé. Vérifiez votre boîte mail.';
        } else if (errorMsg.includes('password') && (errorMsg.includes('weak') || errorMsg.includes('invalid'))) {
          code = 'WEAK_PASSWORD';
          message = 'Le mot de passe ne respecte pas les critères de sécurité';
        } else if (errorMsg.includes('validation') || errorMsg.includes('invalid')) {
          code = 'VALIDATION_ERROR';
          message = apiError.message || 'Données invalides';
        } else {
          code = 'VALIDATION_ERROR';
          message = apiError.message || 'Données invalides';
        }
        break;
      }

      case 401:
        if (context === 'login') {
          code = 'INVALID_CREDENTIALS';
          message = 'Email ou mot de passe incorrect';
        } else {
          code = 'TOKEN_EXPIRED';
          message = 'Session expirée, veuillez vous reconnecter';
        }
        break;

      case 422: {
        // Analyser le message d'erreur pour déterminer le type spécifique
        const errorMsg = apiError.message.toLowerCase();
        
        if (errorMsg.includes('email not verified')) {
          code = 'EMAIL_NOT_VERIFIED';
          message = 'Votre email n\'a pas été validé. Vérifiez votre boîte mail.';
        } else if (errorMsg.includes('email already registered') || errorMsg.includes('already exists')) {
          code = 'EMAIL_ALREADY_EXISTS';
          message = 'Un compte existe déjà avec cet email';
        } else if (errorMsg.includes('password') && (errorMsg.includes('weak') || errorMsg.includes('invalid'))) {
          code = 'WEAK_PASSWORD';
          message = 'Le mot de passe ne respecte pas les critères de sécurité';
        } else if (errorMsg.includes('validation')) {
          code = 'VALIDATION_ERROR';
          message = apiError.message || 'Données invalides';
        } else {
          code = 'VALIDATION_ERROR';
          message = apiError.message || 'Données invalides';
        }
        break;
      }

      case 403:
        code = 'PERMISSION_DENIED';
        message = 'Vous n\'avez pas les permissions nécessaires';
        break;

      case 404:
        if (context === 'validateEmail') {
          code = 'TOKEN_EXPIRED';
          message = 'Lien de validation expiré ou invalide';
        } else {
          code = 'NOT_FOUND';
          message = 'Ressource non trouvée';
        }
        break;

      case 429:
        code = 'TOO_MANY_REQUESTS';
        message = 'Trop de tentatives. Veuillez patienter avant de réessayer';
        break;

      case 0:
        code = 'NETWORK_ERROR';
        message = 'Problème de connexion réseau';
        break;

      case 500:
      case 502:
      case 503:
        code = 'SERVER_ERROR';
        message = 'Erreur serveur temporaire, veuillez réessayer';
        break;

      default: {
        // Pour les erreurs non mappées, essayer d'analyser le message
        const msg = apiError.message.toLowerCase();
        if (msg.includes('email already registered')) {
          code = 'EMAIL_ALREADY_EXISTS';
          message = 'Un compte existe déjà avec cet email';
        } else if (msg.includes('email not verified')) {
          code = 'EMAIL_NOT_VERIFIED';
          message = 'Votre email n\'a pas été validé. Vérifiez votre boîte mail.';
        } else {
          code = 'SERVER_ERROR';
          message = apiError.message || 'Une erreur inattendue s\'est produite';
        }
      }
    }

    const authError: AuthError = {
      name: 'AuthError',
      code: code as AuthError['code'],
      message,
      status: apiError.status,
      details: {
        originalError: apiError,
        context,
        timestamp: new Date(),
      },
    };

    return authError;
  }

  /**
   * Utilitaires de validation côté client
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Le mot de passe doit contenir au moins 8 caractères');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une majuscule');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une minuscule');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un chiffre');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un caractère spécial');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  validateName(name: string): boolean {
    return name.length >= 2 && name.length <= 100;
  }

  /**
   * Extraction des informations du token JWT (sans vérification de signature)
   * Utilisé uniquement pour obtenir l'expiration côté client
   */
  parseJwtToken(token: string): { exp?: number; iat?: number; sub?: string } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch {
      return null;
    }
  }

  /**
   * Vérification de l'expiration du token
   */
  isTokenExpired(token: string): boolean {
    const payload = this.parseJwtToken(token);
    if (!payload?.exp) return true;
    
    // Buffer de 5 minutes avant expiration
    const bufferSeconds = 5 * 60;
    const expirationTime = payload.exp - bufferSeconds;
    const currentTime = Math.floor(Date.now() / 1000);
    
    return currentTime >= expirationTime;
  }

  /**
   * Calcul du temps restant avant expiration (en millisecondes)
   */
  getTimeUntilExpiry(token: string): number | null {
    const payload = this.parseJwtToken(token);
    if (!payload?.exp) return null;
    
    const expirationTime = payload.exp * 1000; // Convertir en millisecondes
    const currentTime = Date.now();
    const timeRemaining = expirationTime - currentTime;
    
    return timeRemaining > 0 ? timeRemaining : 0;
  }
}

// Instance singleton
export const authService = new AuthService();

// Export de la classe pour les tests
export { AuthService };
